import { useEffect, useState } from "react";
import { api, type RuntimeConfig } from "../lib/api";
import { navigate } from "../lib/route";

const HOST = typeof window !== "undefined"
  ? window.location.hostname === "localhost" ? "professionals.test.wiebe.xyz" : window.location.hostname
  : "professionals.wiebe.xyz";

const DEMOS = [
  {
    slug: "lisa-fotografie",
    name: "Lisa van den Berg",
    specialty: "Portrait photography",
    tagline: "Authentic portraits for people who hate being photographed.",
    accent: "#d4a853",
    bg: "#0a0a0a",
    text: "#f5f5f0",
    pages: ["Over mij", "Tarieven"],
  },
  {
    slug: "sophie-brinkman",
    name: "Sophie Brinkman",
    specialty: "Wedding photography",
    tagline: "Wedding photography for the free spirits.",
    accent: "#b45309",
    bg: "#fafaf7",
    text: "#1c1917",
    pages: ["Over mij", "Werkwijze"],
  },
];

export function LandingPage() {
  const [cfg, setCfg] = useState<RuntimeConfig | null>(null);
  const [signingIn, setSigningIn] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.runtimeConfig()
      .then((c) => { if (!cancelled) setCfg(c); })
      .catch(() => { if (!cancelled) setCfg(null); });
    return () => { cancelled = true; };
  }, []);

  async function handleSignIn() {
    if (signingIn) return;
    setSigningIn(true);
    if (!cfg?.oidc?.enabled) {
      navigate("/login");
      return;
    }
    try {
      const { url } = await api.oidcLoginUrl();
      window.location.assign(url);
    } catch {
      window.location.assign(cfg.oidc.login_url || "/api/v1/auth/oidc/login");
    }
  }

  return (
    <>
      <style>{`
        .lp-root { font-family: system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif; color: #0f172a; background: #fff; margin: 0; }
        .lp-nav { position: sticky; top: 0; z-index: 100; background: rgba(255,255,255,.92); backdrop-filter: blur(8px); border-bottom: 1px solid #e2e8f0; display: flex; align-items: center; justify-content: space-between; padding: .875rem 1.5rem; }
        .lp-brand { font-size: 1.05rem; font-weight: 700; letter-spacing: -.01em; }
        .lp-brand span { color: #10b981; }
        .lp-nav-right { display: flex; gap: .75rem; align-items: center; }
        .lp-btn-ghost { padding: .45rem 1rem; border: 1px solid #e2e8f0; border-radius: .4rem; background: none; cursor: pointer; font-size: .875rem; color: #475569; }
        .lp-btn-ghost:hover { background: #f8fafc; }
        .lp-btn-primary { padding: .5rem 1.25rem; background: #10b981; color: #fff; border: none; border-radius: .4rem; cursor: pointer; font-size: .875rem; font-weight: 600; }
        .lp-btn-primary:hover { background: #059669; }
        .lp-btn-primary:disabled { opacity: .6; }

        /* Hero */
        .lp-hero { max-width: 900px; margin: 0 auto; padding: 5rem 1.5rem 3rem; text-align: center; }
        .lp-hero h1 { font-size: clamp(2rem, 5vw, 3.25rem); font-weight: 800; line-height: 1.1; letter-spacing: -.03em; margin: 0 0 1.25rem; }
        .lp-hero p { font-size: 1.15rem; color: #64748b; max-width: 600px; margin: 0 auto 2.5rem; line-height: 1.6; }
        .lp-hero-cta { display: flex; gap: 1rem; justify-content: center; flex-wrap: wrap; }
        .lp-hero-cta button { padding: .75rem 2rem; font-size: 1rem; border-radius: .5rem; cursor: pointer; font-weight: 600; border: none; }
        .lp-hero-cta .primary { background: #10b981; color: #fff; }
        .lp-hero-cta .primary:hover { background: #059669; }
        .lp-hero-cta .secondary { background: none; border: 1.5px solid #e2e8f0; color: #475569; }
        .lp-hero-cta .secondary:hover { background: #f8fafc; }

        /* Demo section */
        .lp-demos { background: #f8fafc; padding: 5rem 1.5rem; }
        .lp-demos-inner { max-width: 1100px; margin: 0 auto; }
        .lp-demos-header { text-align: center; margin-bottom: 3rem; }
        .lp-demos-header h2 { font-size: clamp(1.5rem, 3vw, 2.25rem); font-weight: 700; margin: 0 0 .75rem; letter-spacing: -.02em; }
        .lp-demos-header p { color: #64748b; font-size: 1.05rem; max-width: 500px; margin: 0 auto; }
        .lp-demos-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(460px, 1fr)); gap: 2rem; }

        .lp-demo-card { border-radius: 1rem; overflow: hidden; box-shadow: 0 4px 24px rgba(0,0,0,.08); background: #fff; transition: transform .2s, box-shadow .2s; }
        .lp-demo-card:hover { transform: translateY(-3px); box-shadow: 0 8px 32px rgba(0,0,0,.12); }
        .lp-demo-preview { height: 280px; overflow: hidden; position: relative; }
        .lp-demo-preview iframe { width: 140%; height: 140%; transform: scale(.714); transform-origin: 0 0; pointer-events: none; border: none; }
        .lp-demo-info { padding: 1.25rem 1.5rem 1.5rem; }
        .lp-demo-specialty { font-size: .75rem; font-weight: 700; letter-spacing: .08em; text-transform: uppercase; margin-bottom: .35rem; }
        .lp-demo-name { font-size: 1.15rem; font-weight: 700; margin: 0 0 .35rem; }
        .lp-demo-tagline { font-size: .875rem; color: #64748b; margin: 0 0 1.25rem; }
        .lp-demo-links { display: flex; gap: .5rem; flex-wrap: wrap; align-items: center; }
        .lp-demo-cta { display: inline-block; padding: .5rem 1.25rem; border-radius: .4rem; font-size: .875rem; font-weight: 600; text-decoration: none; color: #fff; }
        .lp-demo-cta:hover { opacity: .88; text-decoration: none; }
        .lp-demo-page-link { font-size: .8rem; color: #64748b; text-decoration: none; padding: .3rem .6rem; border-radius: .3rem; border: 1px solid #e2e8f0; }
        .lp-demo-page-link:hover { background: #f1f5f9; color: #0f172a; text-decoration: none; }

        /* Features */
        .lp-features { max-width: 1000px; margin: 0 auto; padding: 5rem 1.5rem; }
        .lp-features h2 { text-align: center; font-size: clamp(1.5rem, 3vw, 2rem); font-weight: 700; margin: 0 0 3rem; letter-spacing: -.02em; }
        .lp-features-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(280px, 1fr)); gap: 2rem; }
        .lp-feature { padding: 1.5rem; border-radius: .75rem; border: 1px solid #e2e8f0; }
        .lp-feature-icon { font-size: 1.5rem; margin-bottom: .75rem; }
        .lp-feature h3 { font-size: 1rem; font-weight: 700; margin: 0 0 .5rem; }
        .lp-feature p { font-size: .875rem; color: #64748b; margin: 0; line-height: 1.55; }

        /* CTA section */
        .lp-cta { text-align: center; padding: 5rem 1.5rem; background: #0f172a; color: #fff; }
        .lp-cta h2 { font-size: clamp(1.75rem, 4vw, 2.75rem); font-weight: 800; margin: 0 0 1rem; letter-spacing: -.02em; }
        .lp-cta p { color: #94a3b8; margin: 0 0 2rem; font-size: 1.05rem; }
        .lp-cta button { padding: .85rem 2.5rem; font-size: 1.05rem; border-radius: .5rem; background: #10b981; color: #fff; border: none; cursor: pointer; font-weight: 700; }
        .lp-cta button:hover { background: #059669; }

        .lp-footer { padding: 1.5rem; display: flex; justify-content: space-between; align-items: center; border-top: 1px solid #e2e8f0; font-size: .875rem; color: #94a3b8; flex-wrap: wrap; gap: .5rem; }
        .lp-footer a { color: #94a3b8; text-decoration: none; }
        .lp-footer a:hover { color: #475569; }

        @media (max-width: 640px) {
          .lp-demos-grid { grid-template-columns: 1fr; }
          .lp-demo-preview { height: 200px; }
        }
      `}</style>

      <div className="lp-root">
        <nav className="lp-nav">
          <div className="lp-brand">
            Profes<span>sionals</span>
          </div>
          <div className="lp-nav-right">
            <button className="lp-btn-ghost" onClick={handleSignIn} disabled={signingIn}>Sign in</button>
            <button className="lp-btn-primary" onClick={handleSignIn} disabled={signingIn}>Get started free</button>
          </div>
        </nav>

        {/* Hero */}
        <section className="lp-hero">
          <h1>Your photography portfolio,<br />live in 60 seconds.</h1>
          <p>
            A professional portfolio site, private client galleries, booking management,
            and a built-in CRM — everything a photographer needs, nothing they don't.
          </p>
          <div className="lp-hero-cta">
            <button className="primary" onClick={handleSignIn} disabled={signingIn}>
              {signingIn ? "Signing in…" : "Create your portfolio →"}
            </button>
            <button className="secondary" onClick={() => document.getElementById("lp-demos")?.scrollIntoView({behavior:"smooth"})}>
              See examples
            </button>
          </div>
        </section>

        {/* Demo portfolios */}
        <section className="lp-demos" id="lp-demos">
          <div className="lp-demos-inner">
            <div className="lp-demos-header">
              <h2>See what you can build</h2>
              <p>Live demo portfolios — click through to explore the full sites.</p>
            </div>
            <div className="lp-demos-grid">
              {DEMOS.map((demo) => (
                <div className="lp-demo-card" key={demo.slug}>
                  <div className="lp-demo-preview" style={{ background: demo.bg }}>
                    <iframe
                      src={`https://${HOST}/${demo.slug}`}
                      title={demo.name}
                      loading="lazy"
                      sandbox="allow-same-origin"
                    />
                  </div>
                  <div className="lp-demo-info">
                    <div className="lp-demo-specialty" style={{ color: demo.accent }}>{demo.specialty}</div>
                    <div className="lp-demo-name">{demo.name}</div>
                    <div className="lp-demo-tagline">{demo.tagline}</div>
                    <div className="lp-demo-links">
                      <a
                        className="lp-demo-cta"
                        style={{ background: demo.accent }}
                        href={`https://${HOST}/${demo.slug}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        View portfolio ↗
                      </a>
                      {demo.pages.map((page) => (
                        <a
                          key={page}
                          className="lp-demo-page-link"
                          href={`https://${HOST}/${demo.slug}/${page.toLowerCase().replace(/\s+/g, '-').replace(/\//g, '-')}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {page}
                        </a>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="lp-features">
          <h2>Everything you need. Nothing you don't.</h2>
          <div className="lp-features-grid">
            {[
              { icon: "🌐", title: "Portfolio site", desc: "Full-bleed hero, multiple pages, your own slug. Choose from editorial (dark) or minimal (light) templates." },
              { icon: "📸", title: "Client galleries", desc: "Upload photos and share a private link. Download watermarks, OG cards for social, delivery reminders — all included." },
              { icon: "📋", title: "Booking pipeline", desc: "From first inquiry to delivered gallery in one workflow. Email confirmations, delivery dates, payment tracking." },
              { icon: "🎨", title: "Page builder", desc: "Full-screen images, pricing cards, process steps, testimonials — all blocks, no code." },
              { icon: "📬", title: "Lead inbox", desc: "Contact forms on your portfolio feed directly into your inbox. Convert leads to clients with one click." },
              { icon: "🇪🇺", title: "EU-first", desc: "Hosted in Europe. GDPR-friendly. EU e-invoicing (ZUGFeRD, Factur-X) on the roadmap." },
            ].map((f) => (
              <div className="lp-feature" key={f.title}>
                <div className="lp-feature-icon">{f.icon}</div>
                <h3>{f.title}</h3>
                <p>{f.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Final CTA */}
        <section className="lp-cta">
          <h2>Your portfolio, live today.</h2>
          <p>Set up your site in under 5 minutes. No credit card required.</p>
          <button onClick={handleSignIn} disabled={signingIn}>
            {signingIn ? "Signing in…" : "Get started free →"}
          </button>
        </section>

        <footer className="lp-footer">
          <div>© {new Date().getFullYear()} Professionals</div>
          <div style={{ display: "flex", gap: "1rem" }}>
            {cfg?.iambarn?.issuer && <a href={cfg.iambarn.issuer + "/admin"}>Admin</a>}
            <a href="/contact">Contact</a>
          </div>
        </footer>
      </div>
    </>
  );
}
